import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../components/Toast';
import type { DashboardSummary } from '../types';

export interface ExportFilters {
  dateFrom?:   string;
  dateTo?:     string;
  month?:      string;
  year?:       string;
  status?:     string;
  department?: string;
  priority?:   string;
}

export function useReportsSummary() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<DashboardSummary>('/reports/summary');
      setSummary(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refetch(); }, [refetch]);

  return { summary, loading, refetch };
}

function buildParams(format: string, filters: ExportFilters) {
  const p = new URLSearchParams({ format });
  Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
  return p.toString();
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a   = Object.assign(document.createElement('a'), { href: url, download: filename });
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function useExportTickets() {
  const { toast } = useToast();
  const [exporting, setExporting] = useState(false);

  const exportTickets = async (format: 'excel' | 'csv' | 'pdf', filters: ExportFilters = {}) => {
    setExporting(true);
    try {
      const res = await api.get(`/reports/export/tickets?${buildParams(format, filters)}`, {
        responseType: 'blob',
      });
      const ext = format === 'excel' ? 'xlsx' : format;
      triggerDownload(res.data, `permus-tickets.${ext}`);
      toast(`Tickets exported as ${format.toUpperCase()}`, 'success');
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  return { exportTickets, exporting };
}

export function useExportTasks() {
  const { toast } = useToast();
  const [exporting, setExporting] = useState(false);

  const exportTasks = async (format: 'excel' | 'csv', filters: ExportFilters = {}) => {
    setExporting(true);
    try {
      const res = await api.get(`/reports/export/tasks?${buildParams(format, filters)}`, {
        responseType: 'blob',
      });
      const ext = format === 'excel' ? 'xlsx' : format;
      triggerDownload(res.data, `permus-tasks.${ext}`);
      toast(`Tasks exported as ${format.toUpperCase()}`, 'success');
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  return { exportTasks, exporting };
}
